import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaCheckCircle,
  FaPlus,
  FaTimes,
} from "react-icons/fa";
import { processFlyerFile, submitCommunityEvent } from "./eventsApi";
import "./PostEventSheet.css";

/* =========================================================
   PostEventSheet   the "Post event" flow for community pages.
   Opens as a bottom sheet (same ci-sheet anatomy as the
   business and event detail cards). Submits to POST /api/events
   -> lands in the admin dashboard's pending tab.

     <PostEventSheet
       community="Indian"
       communitySlug="indian"
       onClose={() => …}
     />
   ========================================================= */

const TAG_OPTIONS = [
  "Free",
  "Family friendly",
  "Tickets",
  "Food",
  "Live music",
  "Kids activities",
];

const MAX_FLYERS = 1;

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

/* When opened from the hub (no community context), pass
   askCommunity   the form starts with a community picker. The
   options come from communityDirectoryConfig when available. */
const FALLBACK_COMMUNITIES = [
  { label: "Indian", slug: "indian" },
  { label: "Mexican", slug: "mexican" },
  { label: "African", slug: "african" },
  { label: "Americana", slug: "american" },
  { label: "Chinese", slug: "chinese" },
  { label: "Nigerian", slug: "nigerian" },
  { label: "Ethiopian", slug: "ethiopian" },
];

function loadCommunityOptions() {
  try {
    /* eslint-disable-next-line global-require */
    const cfg = require("./communityDirectoryConfig");
    const list =
      cfg.COMMUNITIES || cfg.communities || cfg.COMMUNITY_LIST || cfg.default;
    if (Array.isArray(list) && list.length) {
      const mapped = list
        .map((c) => ({
          label: c.label || c.name || c.people || c.value || "",
          slug: String(c.slug || c.value || c.label || "")
            .trim()
            .toLowerCase(),
        }))
        .filter((c) => c.label && c.slug);
      if (mapped.length) return mapped;
    }
  } catch {
    /* config shape unknown   fallback below */
  }
  return FALLBACK_COMMUNITIES;
}

export default function PostEventSheet({
  community = "Community",
  communitySlug = "",
  initialEmail = "",
  askCommunity = false,
  onClose = null,
}) {
  const [form, setForm] = useState({
    title: "",
    event_date: "",
    start_time: "",
    end_time: "",
    venue_name: "",
    address: "",
    description: "",
    ticket_url: "",
    organizer_name: "",
    contact_email: initialEmail || "",
  });
  const [tags, setTags] = useState([]);

const [scheduleType, setScheduleType] = useState("single");

const [multiOccurrences, setMultiOccurrences] = useState([
  {
    date: "",
    start_time: "",
    end_time: "",
  },
]);

const [recurrence, setRecurrence] = useState({
  frequency: "weekly",
  interval: "1",
  days: [],
  start_date: "",
  start_time: "",
  end_time: "",
  end_mode: "date",
  end_date: "",
  count: "8",
});

const [pickedSlugs, setPickedSlugs] = useState([]);

const communityOptions = askCommunity ? loadCommunityOptions() : [];

const activeSlugs = askCommunity
  ? pickedSlugs
  : [communitySlug || community].filter(Boolean);

const activeSlug = activeSlugs[0] || "";

const selectedCommunityLabels = pickedSlugs
  .filter((slug) => slug !== "all")
  .map(
    (slug) =>
      communityOptions.find((option) => option.slug === slug)?.label || slug
  );

const communityPickerLabel = pickedSlugs.includes("all")
  ? "All Communities"
  : selectedCommunityLabels.length === 0
  ? "Pick one or more communities…"
  : selectedCommunityLabels.length === 1
  ? selectedCommunityLabels[0]
  : `${selectedCommunityLabels.length} communities selected`;

const successAudience = askCommunity
  ? pickedSlugs.includes("all")
    ? "all communities"
    : selectedCommunityLabels.length === 1
    ? `the ${selectedCommunityLabels[0]} community`
    : `${selectedCommunityLabels.join(", ")} communities`
  : `the ${community} community`;

const toggleCommunity = (slug) => {
  setPickedSlugs((prev) => {
    if (slug === "all") {
      return prev.includes("all") ? [] : ["all"];
    }

    const withoutAll = prev.filter((value) => value !== "all");

    if (withoutAll.includes(slug)) {
      return withoutAll.filter((value) => value !== slug);
    }

    return [...withoutAll, slug];
  });

  setErrors((prev) => ({
    ...prev,
    community: "",
    community_slug: "",
  }));
};
const changeScheduleType = (type) => {
  setScheduleType(type);

  if (type === "multi") {
    setMultiOccurrences((prev) => {
      const alreadyStarted = prev.some(
        (item) => item.date || item.start_time || item.end_time
      );

      if (alreadyStarted) return prev;

      return [
        {
          date: form.event_date,
          start_time: form.start_time,
          end_time: form.end_time,
        },
      ];
    });
  }

  if (type === "recurring") {
    setRecurrence((prev) => ({
      ...prev,
      start_date: prev.start_date || form.event_date,
      start_time: prev.start_time || form.start_time,
      end_time: prev.end_time || form.end_time,
    }));
  }

  setErrors((prev) => ({
    ...prev,
    event_date: "",
    start_time: "",
    end_time: "",
    occurrences: "",
    recurrence: "",
  }));
};

const updateMultiOccurrence = (index, field, value) => {
  setMultiOccurrences((prev) =>
    prev.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            [field]: value,
          }
        : item
    )
  );

  setErrors((prev) => ({
    ...prev,
    occurrences: "",
  }));
};

const addMultiOccurrence = () => {
  setMultiOccurrences((prev) => {
    if (prev.length >= 50) return prev;

    return [
      ...prev,
      {
        date: "",
        start_time: "",
        end_time: "",
      },
    ];
  });
};

const removeMultiOccurrence = (index) => {
  setMultiOccurrences((prev) => {
    if (prev.length <= 1) return prev;

    return prev.filter((_, itemIndex) => itemIndex !== index);
  });
};

const setRecurrenceField = (field, value) => {
  setRecurrence((prev) => ({
    ...prev,
    [field]: value,
  }));

  setErrors((prev) => ({
    ...prev,
    recurrence: "",
  }));
};

const toggleRecurrenceDay = (day) => {
  setRecurrence((prev) => ({
    ...prev,
    days: prev.days.includes(day)
      ? prev.days.filter((item) => item !== day)
      : [...prev.days, day],
  }));

  setErrors((prev) => ({
    ...prev,
    recurrence: "",
  }));
};
  const [flyers, setFlyers] = useState([]); /* [{dataUrl, kb}] */
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const isDirty =
    form.title || form.description || form.venue_name || flyers.length > 0;

  const close = () => {
    if (submitting) return;
    if (isDirty && !success) {
      const sure = window.confirm(
        "Discard this event? Your details won't be saved."
      );
      if (!sure) return;
    }
    if (typeof onClose === "function") onClose();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting, isDirty, success]);

  const setField = (name) => (event) => {
    setForm((prev) => ({ ...prev, [name]: event.target.value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const toggleTag = (tag) => {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const pickFlyers = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ""; /* allow re-picking the same file */
    if (files.length === 0) return;

    const room = MAX_FLYERS - flyers.length;
    const chosen = files.slice(0, room);
    if (files.length > room) {
      setBanner("One flyer per event   extra files were skipped.");
    }

    setCompressing(true);
    setErrors((prev) => ({ ...prev, images: "" }));

    const added = [];
    for (const file of chosen) {
      try {
        const dataUrl = await processFlyerFile(file);
        added.push({
          dataUrl,
          kb: Math.round((dataUrl.length * 3) / 4 / 1024),
        });
      } catch {
        setBanner(
          `Couldn't read "${file.name}"   try a JPG, PNG, or PDF flyer.`
        );
      }
    }

    setFlyers((prev) => [...prev, ...added].slice(0, MAX_FLYERS));
    setCompressing(false);
  };

  const removeFlyer = (index) => {
    setFlyers((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const next = {};
  if (askCommunity && pickedSlugs.length === 0)
  next.community = "Pick at least one community or choose All Communities.";
    if (!form.title.trim()) next.title = "Give your event a title.";
  if (scheduleType === "single") {
  if (!form.event_date) {
    next.event_date = "Pick the event date.";
  } else if (form.event_date < todayIso()) {
    next.event_date = "The event date has already passed.";
  }

  if (!form.start_time) {
    next.start_time = "Pick a start time.";
  }
}

if (scheduleType === "multi") {
  if (!multiOccurrences.length) {
    next.occurrences = "Add at least one event date.";
  } else {
    const missingDate = multiOccurrences.findIndex(
      (item) => !item.date
    );

    const pastDate = multiOccurrences.findIndex(
      (item) => item.date && item.date < todayIso()
    );

    const missingTime = multiOccurrences.findIndex(
      (item) => !item.start_time
    );

    if (missingDate >= 0) {
      next.occurrences = `Day ${missingDate + 1} needs a date.`;
    } else if (pastDate >= 0) {
      next.occurrences = `Day ${pastDate + 1} has already passed.`;
    } else if (missingTime >= 0) {
      next.occurrences = `Day ${missingTime + 1} needs a start time.`;
    }
  }
}

if (scheduleType === "recurring") {
  const interval = Number(recurrence.interval);

  if (!recurrence.start_date) {
    next.recurrence = "Pick the recurring start date.";
  } else if (recurrence.start_date < todayIso()) {
    next.recurrence = "The recurring start date has already passed.";
  } else if (!recurrence.start_time) {
    next.recurrence = "Pick a start time.";
  } else if (!interval || interval < 1 || interval > 30) {
    next.recurrence = "Repeat interval must be between 1 and 30.";
  } else if (
    recurrence.frequency === "weekly" &&
    recurrence.days.length === 0
  ) {
    next.recurrence = "Choose at least one day of the week.";
  } else if (recurrence.end_mode === "date") {
    if (!recurrence.end_date) {
      next.recurrence = "Pick when the recurring event ends.";
    } else if (recurrence.end_date < recurrence.start_date) {
      next.recurrence =
        "The recurring end date cannot be before the start date.";
    }
  } else {
    const count = Number(recurrence.count);

    if (!count || count < 1 || count > 366) {
      next.recurrence =
        "Number of occurrences must be between 1 and 366.";
    }
  }
}
    if (!form.venue_name.trim())
      next.venue_name = "Where is it happening?";
    if (!form.address.trim()) next.address = "Add the street address.";
    if (!form.organizer_name.trim())
      next.organizer_name = "Who's organizing this event?";
    if (
      form.contact_email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim())
    )
      next.contact_email = "Enter a valid email.";
    return next;
  };

  const submit = async () => {
    setBanner("");
    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    let ticketUrl = form.ticket_url.trim();
    if (ticketUrl && !/^https?:\/\//i.test(ticketUrl)) {
      ticketUrl = `https://${ticketUrl}`;
    }

    let schedulePayload = {
  schedule_type: "single",
};

if (scheduleType === "multi") {
  schedulePayload = {
    schedule_type: "multi",
    occurrences: multiOccurrences.map((item) => ({
      date: item.date,
      start_time: item.start_time,
      end_time: item.end_time,
    })),
  };
}

if (scheduleType === "recurring") {
  schedulePayload = {
    schedule_type: "recurring",

    recurrence: {
      frequency: recurrence.frequency,
      interval: Number(recurrence.interval) || 1,

      days:
        recurrence.frequency === "weekly"
          ? recurrence.days
          : [],

      start_date: recurrence.start_date,
      start_time: recurrence.start_time,
      end_time: recurrence.end_time,

      ...(recurrence.end_mode === "date"
        ? {
            end_date: recurrence.end_date,
          }
        : {
            count: Number(recurrence.count) || 1,
          }),
    },
  };
}

    setSubmitting(true);
    try {
      const data = await submitCommunityEvent({
        community_slug: activeSlug,
community_slugs: activeSlugs,
        title: form.title.trim(),
        description: form.description.trim(),
        schedule_type: scheduleType,
...schedulePayload,

event_date:
  scheduleType === "single" ? form.event_date : "",

start_time:
  scheduleType === "single" ? form.start_time : "",

end_time:
  scheduleType === "single" ? form.end_time : "",
        venue_name: form.venue_name.trim(),
        address: form.address.trim(),
        ticket_url: ticketUrl,
        organizer_name: form.organizer_name.trim(),
        contact_email: form.contact_email.trim(),
        tags,
        images: flyers.map((flyer) => flyer.dataUrl),
      });
      setSuccess({ eventId: data?.eventId });
    } catch (err) {
      const payload = err?.response?.data;
      if (payload?.code === "DUPLICATE_EVENT") {
        setBanner(
          "A matching event is already listed or waiting for review."
        );
      } else if (payload?.errors) {
        setErrors(payload.errors);
        setBanner("Please fix the highlighted fields.");
      } else {
        setBanner(
          payload?.message ||
            "Couldn't submit right now   please try again in a moment."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="ci-sheetwrap"
      role="dialog"
      aria-modal="true"
      aria-label={`Post a ${community} community event`}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="ci-sheet pef">
        <button
          type="button"
          className="ci-sheet__x"
          onClick={close}
          aria-label="Close"
        >
          <FaTimes />
        </button>

        {success ? (
          <div className="pef-success">
            <span className="pef-success__ic" aria-hidden="true">
              <FaCheckCircle />
            </span>
            <h2 className="ci-sheet__nm">Event submitted!</h2>
            <p>
              Thanks for sharing with {successAudience}. Our team gives
              every event a quick review   once approved, it appears on the
              community page and in Upcoming events.
            </p>
            {success.eventId && (
              <p className="pef-success__ref">
                Reference #{success.eventId}
              </p>
            )}
            <button
              type="button"
              className="pef-submit"
              onClick={() => onClose && onClose()}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <span className="pef-kicker">
              {askCommunity ? "Community event" : `${community} community`}
            </span>
            <h2 className="ci-sheet__nm">Post an event</h2>
            <p className="pef-sub">
              Free to post · reviewed by GoOurHub before it appears.
            </p>

            {banner && (
              <p className="pef-banner" role="alert">
                {banner}
              </p>
            )}
{askCommunity && (
  <div className="pef-field">
    <span>Community *</span>

    <details className="pef-community-multi">
      <summary className="pef-community-summary">
        {communityPickerLabel}
      </summary>

      <div className="pef-community-options">
        <label className="pef-community-option pef-community-option--all">
          <input
            type="checkbox"
            checked={pickedSlugs.includes("all")}
            onChange={() => toggleCommunity("all")}
          />
          <span>All Communities</span>
        </label>

        {communityOptions.map((option) => (
          <label
            key={option.slug}
            className="pef-community-option"
          >
            <input
              type="checkbox"
              checked={pickedSlugs.includes(option.slug)}
              onChange={() => toggleCommunity(option.slug)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </details>

    {errors.community && <em>{errors.community}</em>}
  </div>
)}

            <label className="pef-field">
              <span>Event title *</span>
              <input
                type="text"
                maxLength={160}
                value={form.title}
                onChange={setField("title")}
                placeholder="Diwali Mela 2026"
              />
              {errors.title && <em>{errors.title}</em>}
            </label>

            <div className="pef-field">
  <span>When is this event? *</span>

  <div
    className="pef-schedule-tabs"
    role="group"
    aria-label="Event schedule type"
  >
    <button
      type="button"
      className={scheduleType === "single" ? "is-on" : ""}
      aria-pressed={scheduleType === "single"}
      onClick={() => changeScheduleType("single")}
    >
      Single day
    </button>

    <button
      type="button"
      className={scheduleType === "multi" ? "is-on" : ""}
      aria-pressed={scheduleType === "multi"}
      onClick={() => changeScheduleType("multi")}
    >
      Multiple days
    </button>

    <button
      type="button"
      className={scheduleType === "recurring" ? "is-on" : ""}
      aria-pressed={scheduleType === "recurring"}
      onClick={() => changeScheduleType("recurring")}
    >
      Recurring
    </button>
  </div>
</div>

{scheduleType === "single" && (
  <>
    <label className="pef-field">
      <span>Date *</span>
      <input
        type="date"
        min={todayIso()}
        value={form.event_date}
        onChange={setField("event_date")}
      />
      {errors.event_date && <em>{errors.event_date}</em>}
    </label>

    <div className="pef-row">
      <label className="pef-field">
        <span>Starts *</span>
        <input
          type="time"
          value={form.start_time}
          onChange={setField("start_time")}
        />
        {errors.start_time && <em>{errors.start_time}</em>}
      </label>

      <label className="pef-field">
        <span>Ends</span>
        <input
          type="time"
          value={form.end_time}
          onChange={setField("end_time")}
        />
        {errors.end_time && <em>{errors.end_time}</em>}
      </label>
    </div>
  </>
)}

{scheduleType === "multi" && (
  <div className="pef-schedule-panel">
    <div className="pef-schedule-head">
      <div>
        <strong>Multiple event days</strong>
        <small>Add the date and time for each day.</small>
      </div>
    </div>

    {multiOccurrences.map((occurrence, index) => (
      <div className="pef-occurrence" key={index}>
        <div className="pef-occurrence__head">
          <strong>Day {index + 1}</strong>

          {multiOccurrences.length > 1 && (
            <button
              type="button"
              className="pef-occurrence__remove"
              onClick={() => removeMultiOccurrence(index)}
            >
              Remove
            </button>
          )}
        </div>

        <label className="pef-field">
          <span>Date *</span>
          <input
            type="date"
            min={todayIso()}
            value={occurrence.date}
            onChange={(event) =>
              updateMultiOccurrence(
                index,
                "date",
                event.target.value
              )
            }
          />
        </label>

        <div className="pef-row">
          <label className="pef-field">
            <span>Starts *</span>
            <input
              type="time"
              value={occurrence.start_time}
              onChange={(event) =>
                updateMultiOccurrence(
                  index,
                  "start_time",
                  event.target.value
                )
              }
            />
          </label>

          <label className="pef-field">
            <span>Ends</span>
            <input
              type="time"
              value={occurrence.end_time}
              onChange={(event) =>
                updateMultiOccurrence(
                  index,
                  "end_time",
                  event.target.value
                )
              }
            />
          </label>
        </div>
      </div>
    ))}

    {errors.occurrences && (
      <em className="pef-err">{errors.occurrences}</em>
    )}

    <button
      type="button"
      className="pef-add-date"
      onClick={addMultiOccurrence}
      disabled={multiOccurrences.length >= 50}
    >
      + Add another date
    </button>
  </div>
)}

{scheduleType === "recurring" && (
  <div className="pef-schedule-panel">
    <div className="pef-schedule-head">
      <div>
        <strong>Recurring event</strong>
        <small>Set how often this event repeats.</small>
      </div>
    </div>

    <div className="pef-row">
      <label className="pef-field">
        <span>Repeats *</span>
        <select
          value={recurrence.frequency}
          onChange={(event) =>
            setRecurrenceField(
              "frequency",
              event.target.value
            )
          }
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </label>

      <label className="pef-field">
        <span>Every *</span>
        <input
          type="number"
          min="1"
          max="30"
          value={recurrence.interval}
          onChange={(event) =>
            setRecurrenceField(
              "interval",
              event.target.value
            )
          }
        />
        <small className="pef-hint">
          {recurrence.frequency === "daily"
            ? "day(s)"
            : recurrence.frequency === "weekly"
            ? "week(s)"
            : "month(s)"}
        </small>
      </label>
    </div>

    {recurrence.frequency === "weekly" && (
      <div className="pef-field">
        <span>Repeats on *</span>

        <div className="pef-weekdays">
          {[
            ["sunday", "Sun"],
            ["monday", "Mon"],
            ["tuesday", "Tue"],
            ["wednesday", "Wed"],
            ["thursday", "Thu"],
            ["friday", "Fri"],
            ["saturday", "Sat"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={
                recurrence.days.includes(value)
                  ? "is-on"
                  : ""
              }
              aria-pressed={recurrence.days.includes(value)}
              onClick={() => toggleRecurrenceDay(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    )}

    <label className="pef-field">
      <span>Starts on *</span>
      <input
        type="date"
        min={todayIso()}
        value={recurrence.start_date}
        onChange={(event) =>
          setRecurrenceField(
            "start_date",
            event.target.value
          )
        }
      />
    </label>

    <div className="pef-row">
      <label className="pef-field">
        <span>Starts *</span>
        <input
          type="time"
          value={recurrence.start_time}
          onChange={(event) =>
            setRecurrenceField(
              "start_time",
              event.target.value
            )
          }
        />
      </label>

      <label className="pef-field">
        <span>Ends</span>
        <input
          type="time"
          value={recurrence.end_time}
          onChange={(event) =>
            setRecurrenceField(
              "end_time",
              event.target.value
            )
          }
        />
      </label>
    </div>

    <label className="pef-field">
      <span>Series ends *</span>
      <select
        value={recurrence.end_mode}
        onChange={(event) =>
          setRecurrenceField(
            "end_mode",
            event.target.value
          )
        }
      >
        <option value="date">On a date</option>
        <option value="count">
          After a number of occurrences
        </option>
      </select>
    </label>

    {recurrence.end_mode === "date" ? (
      <label className="pef-field">
        <span>Last date *</span>
        <input
          type="date"
          min={recurrence.start_date || todayIso()}
          value={recurrence.end_date}
          onChange={(event) =>
            setRecurrenceField(
              "end_date",
              event.target.value
            )
          }
        />
      </label>
    ) : (
      <label className="pef-field">
        <span>Number of occurrences *</span>
        <input
          type="number"
          min="1"
          max="366"
          value={recurrence.count}
          onChange={(event) =>
            setRecurrenceField(
              "count",
              event.target.value
            )
          }
        />
      </label>
    )}

    {errors.recurrence && (
      <em className="pef-err">{errors.recurrence}</em>
    )}
  </div>
)}

            <label className="pef-field">
              <span>Venue *</span>
              <input
                type="text"
                maxLength={160}
                value={form.venue_name}
                onChange={setField("venue_name")}
                placeholder="Market Square"
              />
              {errors.venue_name && <em>{errors.venue_name}</em>}
            </label>

            <label className="pef-field">
              <span>Address *</span>
              <input
                type="text"
                maxLength={300}
                value={form.address}
                onChange={setField("address")}
                placeholder="514 W Commerce St, San Antonio, TX"
              />
              {errors.address && <em>{errors.address}</em>}
            </label>

            <label className="pef-field">
              <span>What's happening?</span>
              <textarea
                rows={3}
                maxLength={2000}
                value={form.description}
                onChange={setField("description")}
                placeholder="Music, food, activities   tell the community what to expect."
              />
            </label>

            <div className="pef-field">
              <span>Tags</span>
              <div className="pef-chips">
                {TAG_OPTIONS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`pef-chip ${tags.includes(tag) ? "is-on" : ""}`}
                    onClick={() => toggleTag(tag)}
                    aria-pressed={tags.includes(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <div className="pef-field">
              <span>Flyer (JPG, PNG, or PDF)</span>
              <div className="pef-flyers">
                {flyers.map((flyer, index) => (
                  <span key={index} className="pef-thumb">
                    <img src={flyer.dataUrl} alt={`Flyer ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => removeFlyer(index)}
                      aria-label={`Remove flyer ${index + 1}`}
                    >
                      <FaTimes />
                    </button>
                    <small>{flyer.kb} KB</small>
                  </span>
                ))}

                {flyers.length < MAX_FLYERS && (
                  <button
                    type="button"
                    className="pef-add"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={compressing}
                  >
                    <FaPlus aria-hidden="true" />
                    {compressing ? "Adding…" : "Add"}
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,.pdf"
                hidden
                onChange={pickFlyers}
              />
              <p className="pef-hint">
                Photos compress automatically; PDF flyers use their first page.
              </p>
              {errors.images && <em className="pef-err">{errors.images}</em>}
            </div>

            <label className="pef-field">
              <span>Ticket / RSVP link</span>
              <input
                type="url"
                maxLength={300}
                value={form.ticket_url}
                onChange={setField("ticket_url")}
                placeholder="https://…"
                inputMode="url"
              />
              {errors.ticket_url && <em>{errors.ticket_url}</em>}
            </label>

            <label className="pef-field">
              <span>Organizer name *</span>
              <input
                type="text"
                maxLength={120}
                value={form.organizer_name}
                onChange={setField("organizer_name")}
                placeholder="SA Indian Association"
              />
              {errors.organizer_name && <em>{errors.organizer_name}</em>}
            </label>

            <label className="pef-field">
              <span>Contact email</span>
              <input
                type="email"
                maxLength={160}
                value={form.contact_email}
                onChange={setField("contact_email")}
                placeholder="you@example.com"
                inputMode="email"
              />
              {errors.contact_email && <em>{errors.contact_email}</em>}
              <p className="pef-hint">
                Only for review questions   never shown publicly.
              </p>
            </label>

            <button
              type="button"
              className="pef-submit"
              onClick={submit}
              disabled={submitting || compressing}
            >
              {submitting ? "Submitting…" : "Submit for review"}
            </button>

            <p className="ci-sheet__disc">
              By posting you confirm you have the right to share these details
              and images. Events are reviewed by GoOurHub before appearing.
            </p>
          </>
        )}
      </div>
    </div>
  , document.body);
}
